import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { LanguageSelector } from './components/LanguageSelector';
import { Footer } from './components/Footer';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900 flex flex-col relative overflow-x-hidden selection:bg-cyan-200 selection:text-cyan-900">
      {/* Navbar */}
      <nav className="w-full py-6 px-8 flex justify-between items-center border-b border-slate-200/60 bg-white/70 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-shadow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-display tracking-tight text-slate-800">SlideDeck<span className="text-cyan-600">Cleaner</span></span>
          </Link>
        </div>
        
        <LanguageSelector />
      </nav>

      <main className="flex-1 max-w-4xl mx-auto p-6 md:p-12 w-full z-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-slate-900">Terms of Service</h1>
        
        <div className="prose prose-slate max-w-none space-y-8 text-slate-600">
          <p className="text-sm text-slate-500">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing and using SlideDeckCleaner ("the Service"), you agree to comply with and be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">2. Description of Service</h2>
            <p>
              SlideDeckCleaner provides a tool to convert files (e.g., PDF) into editable formats (e.g., PowerPoint). 
              The Service operates on a credit-based system. Some features may require the purchase of credits ("Page Credits").
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">3. Payments, Credits, and Refunds</h2>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong>One-Time Purchase:</strong> We offer credits via one-time payments. This is not a subscription service, 
                and you will not be charged recurring fees.
              </li>
              <li>
                <strong>Credit Validity:</strong> Purchased credits do not expire and will remain in your account until used.
              </li>
              <li>
                <strong>Pricing:</strong> We reserve the right to change our pricing at any time. However, price changes will not 
                affect credits you have already purchased.
              </li>
              <li>
                <strong>Refund Policy:</strong> Due to the nature of digital goods, all sales are final. Refunds are generally not provided 
                unless there is a verifiable technical failure that prevents the Service from delivering the converted file. 
                Please contact support if you experience issues.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">4. User Conduct</h2>
            <p>
              You agree not to use the Service for any unlawful purpose. You are solely responsible for the content of the files you upload. 
              You represent and warrant that you own or have the necessary licenses, rights, consents, and permissions to upload and convert your content.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">5. Intellectual Property</h2>
            <p>
              We do not claim ownership of the content you upload. You retain all rights to your original content and the converted output. 
              We do not use your content for any purpose other than providing the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">6. Disclaimer of Warranties</h2>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. While we strive for high accuracy, we cannot guarantee 
              that the conversion will always be 100% perfect or error-free due to the complexity of PDF file structures.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, SlideDeckCleaner shall not be liable for any indirect, incidental, or consequential damages. 
              <strong>In no event shall our total liability to you exceed the amount you paid for the Service.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">8. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. Your continued use of the Service following any changes 
              indicates your acceptance of the new Terms of Service.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;